/**
 * ReportOrderSimple — сборка отчёта в три поля.
 *
 * Та же служебная страница, что и `ReportOrder`, урезанная до того, что
 * специалист меняет каждый раз: какой проект и за какой период. Всё
 * остальное — состав этажей, пороги, доставка — берётся из умолчаний
 * сборки, и полный пульт остаётся на случай, когда умолчания не годятся.
 *
 * Поэтому здесь нет `FloorHeading`: раздел ровно один, и заголовок над
 * единственной карточкой повторял бы титул страницы. Карточка, полоса,
 * плашки состояний и кнопка — те же, что в полной версии, чтобы переход
 * между двумя пультами не читался как переход в другой продукт.
 *
 * Наружу уходит подмножество значений: остальные поля не спрятаны, их тут
 * просто нет, и подставлять за пользователя чужие умолчания страница не
 * берётся — это дело вызывающего кода.
 *
 * @format
 */

import {useRef, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {Spinner} from 'ui/components/atoms/Spinner';
import {FooterActionBar} from 'ui/components/composition/FooterActionBar';
import {NoticeCard} from 'ui/components/composition/NoticeCard';
import {PageHeader} from 'ui/components/composition/PageHeader';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {DatePicker} from 'ui/components/molecules/DatePicker';
import {FormField} from 'ui/components/molecules/FormField';
import {Select} from 'ui/components/molecules/Select';
import {AppHeader} from 'ui/components/organisms/AppHeader';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {ReportCard} from '../ExecutiveReport/components/ReportCard';
import {FORM_MAX_WIDTH} from '../ExecutiveReport/helpers';
import {periodBounds, periodError} from '../ReportOrder/period';
import {
  COMPANY_OPTIONS,
  DEFAULT_ORDER_VALUES,
  type CompanyOption,
  type ReportOrderState,
  type ReportOrderValues,
} from '../ReportOrder/types';

// NBSP в JSX-тексте запрещён линтером — типографированные подписи держим
// строками.
const OPEN_RESULT_LABEL = 'Открыть в хранилище';
const COMPANY_PLACEHOLDER = 'Выберите проект';
const SUBTITLE = 'Проект и период — остальное сборка берёт из умолчаний';

const FAILURE_DEFAULT =
  'Доступ к\u00a0Директу отозван на\u00a0середине периода\u00a0— данных за\u00a0месяц нет. Восстановите доступ и\u00a0запустите сборку\u00a0заново.';

/** То, что страница спрашивает. Остальные параметры сборки задаются вне её. */
export type ReportOrderSimpleValues = Pick<
  ReportOrderValues,
  'companyId' | 'periodFrom' | 'periodTo'
>;

export const DEFAULT_SIMPLE_VALUES: ReportOrderSimpleValues = {
  companyId: DEFAULT_ORDER_VALUES.companyId,
  periodFrom: DEFAULT_ORDER_VALUES.periodFrom,
  periodTo: DEFAULT_ORDER_VALUES.periodTo,
};

export interface ReportOrderSimpleProps {
  /** Стартовые значения. По умолчанию — прошлый закрытый месяц. */
  initialValues?: ReportOrderSimpleValues;
  /** Проекты, из которых выбирают. В хост-приложении приходят с сервера. */
  companies?: CompanyOption[];
  /** Что делает сборка. По умолчанию форма ждёт запуска. */
  state?: ReportOrderState;
  /** Запуск сборки. Получает три значения формы. */
  onSubmit?: (values: ReportOrderSimpleValues) => void;
  /** Открыть собранный отчёт в хранилище — только в состоянии `done`. */
  onOpenResult?: () => void;
  /** Клик по логотипу в шапке приложения. */
  onLogoClick?: () => void;
  /**
   * Чем кончилась сборка. Причина приходит с сервера и бывает нескольких
   * видов, поэтому вписывать её в компонент нельзя; по умолчанию — самая
   * частая.
   */
  failureText?: string;
}

const Page = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  color: surfaceVar('onSurface'),
}));

const Content = styled(Stack)({
  maxWidth: FORM_MAX_WIDTH,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacingPx('l'),
  paddingRight: spacingPx('l'),
  paddingTop: spacingPx('xl') * 2,
  paddingBottom: spacingPx('xl') * 2,
  // Шаг между блоками страницы — тот же удвоенный, что разводит этажи
  // отчёта, хранилища и полного пульта.
  gap: spacingPx('xl') * 2,
});

/** Половина ряда дат: `flex: 1 1 0` делает половины равными. */
const Field = styled(Box)({
  flex: '1 1 0',
  minWidth: 0,
});

/**
 * Сообщение о периоде. Тон — тот же, что у ошибок полей, кегль — тот же,
 * что у подписей под ними: это подпись к паре полей, а не плашка.
 */
const PeriodError = styled(Box)(({theme}) => ({
  ...text('body.m'),
  color: theme.palette.bad.main,
}));

export function ReportOrderSimple({
  initialValues = DEFAULT_SIMPLE_VALUES,
  companies = COMPANY_OPTIONS,
  state = 'idle',
  onSubmit,
  onOpenResult,
  onLogoClick,
  failureText = FAILURE_DEFAULT,
}: ReportOrderSimpleProps) {
  const [values, setValues] = useState<ReportOrderSimpleValues>(initialValues);

  const patch = <K extends keyof ReportOrderSimpleValues>(
    key: K,
    value: ReportOrderSimpleValues[K],
  ) => setValues(prev => ({...prev, [key]: value}));

  // «Сегодня» фиксируется на время жизни формы: `new Date()` прямо в разметке
  // пересчитывался бы на каждый ре-рендер и делал границы календарей
  // нестабильными.
  const today = useRef(new Date()).current;
  const bounds = periodBounds(values.periodFrom, values.periodTo, today);
  const periodProblem = periodError(values.periodFrom, values.periodTo, today);

  const companyOptions = companies.map(company => ({value: company.id, label: company.name}));

  return (
    <Page>
      <AppHeader variant='empty' onLogoClick={onLogoClick} />

      <Content>
        <NoticeCard
          tone='alert'
          icon='engineering'
          title='Служебная страница'
          description='Точки входа из кабинета сюда нет: сборку запускает специалист AdSensor. Клиент видит только готовый отчёт в хранилище.'
        />

        <PageHeader title='Сборка отчёта' subtitle={SUBTITLE} />

        {state === 'building' && (
          <NoticeCard
            tone='primary'
            icon={<Spinner size='l' label={null} />}
            title='Собираем отчёт'
            description='Система разбирает историю изменений за период. Обычно занимает около часа — страницу можно закрыть.'
          />
        )}

        {state === 'done' && (
          <NoticeCard
            tone='good'
            title='Отчёт собран'
            description='Документ лежит в хранилище проекта и доступен клиенту.'
            action={
              onOpenResult != null && (
                <Button variant='outline' color='good' onClick={onOpenResult}>
                  {OPEN_RESULT_LABEL}
                </Button>
              )
            }
          />
        )}

        {state === 'failed' && (
          <NoticeCard tone='bad' title='Сборка прервана' description={failureText} />
        )}

        <ReportCard padding='l'>
          <Stack gap='m'>
            <FormField label='Проект' htmlFor='simple-company' required>
              <Select
                id='simple-company'
                fullWidth
                value={values.companyId}
                onChange={next => patch('companyId', next)}
                options={companyOptions}
                placeholder={COMPANY_PLACEHOLDER}
              />
            </FormField>

            {/* Оба поля и сообщение под ними — одна связка: ошибка говорит
                про период целиком, а не про конкретную дату, поэтому стоит
                под парой, а не под одним из полей. */}
            <Stack gap='xs'>
              <Row gap='m' alignY='start' reflowAt='sm'>
                <Field>
                  <FormField label='Период с' htmlFor='simple-from' required>
                    <DatePicker
                      id='simple-from'
                      fullWidth
                      error={periodProblem != null}
                      value={values.periodFrom}
                      onChange={next => patch('periodFrom', next)}
                      min={bounds.fromMin}
                      max={bounds.fromMax}
                    />
                  </FormField>
                </Field>
                <Field>
                  <FormField label='Период по' htmlFor='simple-to' required>
                    <DatePicker
                      id='simple-to'
                      fullWidth
                      error={periodProblem != null}
                      value={values.periodTo}
                      onChange={next => patch('periodTo', next)}
                      min={bounds.toMin}
                      max={bounds.toMax}
                    />
                  </FormField>
                </Field>
              </Row>

              {/* `role='alert'` обязателен: без него незрячий читатель видит
                  только погасшую кнопку и не знает, чем она погашена. */}
              {periodProblem != null && <PeriodError role='alert'>{periodProblem}</PeriodError>}
            </Stack>
          </Stack>
        </ReportCard>

        <FooterActionBar
          align='end'
          primary={
            <Button
              color='primary'
              leftIcon='play_arrow'
              loading={state === 'building' ? 'left' : false}
              // Кнопка гаснет вместе с появлением текста ошибки: погашенная
              // кнопка без объяснения — тупик, а объяснение без погашенной
              // кнопки читается как необязательное замечание.
              disabled={periodProblem != null}
              onClick={() => onSubmit?.(values)}
            >
              {state === 'failed' ? 'Запустить заново' : 'Собрать отчёт'}
            </Button>
          }
        />
      </Content>
    </Page>
  );
}
